import { useState } from 'react';
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import {
  LayoutDashboard, Users, GraduationCap, School, BookOpen,
  ClipboardList, LogOut, Menu, X, ChevronRight, Building2,
} from 'lucide-react';

const navItems = [
  { path: '/app/dashboard', label: 'Tableau de bord', icon: LayoutDashboard },
  { path: '/app/students', label: 'Eleves', icon: GraduationCap },
  { path: '/app/teachers', label: 'Enseignants', icon: Users },
  { path: '/app/classes', label: 'Classes', icon: School },
  { path: '/app/evaluations', label: 'Evaluations', icon: ClipboardList },
  { path: '/app/users', label: 'Utilisateurs', icon: Users },
];

export default function SchoolLayout() {
  const { user, tenant, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const primaryColor = tenant?.settings?.primary_color || '#16a34a';

  return (
    <div className="min-h-screen bg-[#fafaf8] flex">
      {/* Sidebar - Desktop */}
      <aside className="hidden lg:flex w-60 flex-col bg-white border-r border-stone-200 fixed h-screen">
        <div className="p-4 border-b border-stone-200">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: primaryColor }}>
              <Building2 className="w-4 h-4 text-white" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-bold text-stone-900 truncate">{tenant?.name || 'Ecole'}</p>
              <p className="text-xs text-stone-400">{tenant?.plan || 'free'}</p>
            </div>
          </div>
        </div>

        <nav className="flex-1 p-3 overflow-y-auto space-y-0.5">
          {navItems.map((item) => {
            const active = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${
                  active ? 'bg-stone-100 font-medium text-stone-900' : 'text-stone-500 hover:text-stone-900 hover:bg-stone-50'
                }`}
                data-testid={`nav-${item.path.split('/').pop()}`}
              >
                <item.icon className="w-4 h-4" />
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="p-3 border-t border-stone-200">
          <div className="flex items-center gap-2.5 px-3 py-2">
            <div className="w-7 h-7 rounded-full bg-stone-200 flex items-center justify-center text-xs font-medium text-stone-600">
              {user?.first_name?.[0]}{user?.last_name?.[0]}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-medium text-stone-900 truncate">{user?.first_name} {user?.last_name}</p>
              <p className="text-xs text-stone-400 truncate">{user?.role}</p>
            </div>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-stone-400 h-7 w-7 p-0" data-testid="logout-btn">
              <LogOut className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-stone-200 h-14 flex items-center px-4 justify-between">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: primaryColor }}>
            <Building2 className="w-3.5 h-3.5 text-white" />
          </div>
          <span className="text-sm font-bold text-stone-900">{tenant?.name || 'Ecole'}</span>
        </div>
        <Button variant="ghost" size="sm" onClick={() => setMobileOpen(!mobileOpen)} className="h-8 w-8 p-0">
          {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </Button>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-black/40" onClick={() => setMobileOpen(false)}>
          <div className="absolute right-0 top-14 bottom-0 w-64 bg-white shadow-xl" onClick={e => e.stopPropagation()}>
            <nav className="p-3 space-y-0.5">
              {navItems.map((item) => {
                const active = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileOpen(false)}
                    className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm ${
                      active ? 'bg-stone-100 font-medium text-stone-900' : 'text-stone-500'
                    }`}
                  >
                    <item.icon className="w-4 h-4" />
                    {item.label}
                  </Link>
                );
              })}
            </nav>
            <div className="absolute bottom-0 left-0 right-0 p-3 border-t border-stone-200">
              <Button variant="ghost" size="sm" onClick={handleLogout} className="w-full justify-start text-stone-500">
                <LogOut className="w-4 h-4 mr-2" />
                Deconnexion
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 lg:ml-60 pt-14 lg:pt-0">
        <div className="p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
